import { FC } from 'react';
import { Stack, Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { FormattedMessage } from 'react-intl';
import { MetricValueComparison } from './widgets/metrics/MetricValueComparison';
import { graphql } from '../../../gql';
import { useApiQuery } from '../../../query/hooks';
import { metricFormatValue } from '../../utils/metricFormatValue';
import { getNightOrDayPalette } from '../../../utils/getNightOrDayPalette';

const displayMetricQuery = graphql(`
	query journey_displayPhaseMetric(
		$id: UUID!
		$compareStart: DateTime!
		$compareEnd: DateTime!
		$targetStart: DateTime!
		$targetEnd: DateTime!
	) {
		displayMetric(id: $id) {
			name
			expectations
			precision
			trendComparison
			values(compareRange: { start: $compareStart, end: $compareEnd }, targetRange: { start: $targetStart, end: $targetEnd }) {
				targetValue
				comparison
				lastUpdateAt
			}
		}
	}
`);

type Props = Readonly<{
	metricId?: string;
	compareStart: string;
	compareEnd: string;
	targetStart: string;
	targetEnd: string;
	activities?: string[];
	unitSign: string | null;
}>;

const PhaseMetricCell: FC<Props> = ({ metricId, compareStart, compareEnd, targetStart, targetEnd, activities, unitSign }) => {
	const { data, isLoading } = useApiQuery(displayMetricQuery, {
		variables: {
			id: metricId ?? '',
			compareStart,
			compareEnd,
			targetStart,
			targetEnd,
		},
	});

	if (!metricId) {
		return null;
	}

	if (isLoading) {
		return (
			<Stack minWidth={180} height={'1.7em'} justifyContent={'center'}>
				<LinearProgress />
			</Stack>
		);
	}

	return (
		<Stack width={180}>
			<Typography fontSize={12} fontWeight={500}>
				<Typography
					sx={{
						'-webkit-line-clamp': '2',
						display: '-webkit-box',
						'-webkit-box-orient': 'vertical',
						wordBreak: 'break-word',
						overflow: 'hidden',
					}}
					title={data?.displayMetric?.name}
				>
					{/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
					{data?.displayMetric?.name}{' '}
				</Typography>
				<Typography
					component={'span'}
					color={(theme) => theme.palette[getNightOrDayPalette(theme)][500]}
					sx={{ '&:before': { content: '"("' }, '&:after': { content: '")"' } }}
				>
					{activities?.join(', ')}
				</Typography>
			</Typography>
			{data?.displayMetric?.values.targetValue != null && data.displayMetric.precision != null ? (
				<Typography variant={'h5'} fontSize={18} fontWeight={500} mt={'auto'}>
					{metricFormatValue(data.displayMetric.values.targetValue, data.displayMetric.precision)}&nbsp;{unitSign}
				</Typography>
			) : (
				<Typography variant={'h5'} fontSize={18} fontWeight={500} mt={'auto'}>
					<FormattedMessage id={'common.not-available'} />
				</Typography>
			)}
			{data?.displayMetric != null && (
				<Stack direction={'row'} alignItems={'center'} marginLeft={-0.75}>
					<MetricValueComparison metric={data.displayMetric} />
				</Stack>
			)}
		</Stack>
	);
};

export { PhaseMetricCell };
